import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { createContext, useCallback, useContext, useState } from 'react';

export type SuperHubAppendToRootContextValue = HTMLDivElement | null;

export const SuperHubAppendToRootContext = createContext<SuperHubAppendToRootContextValue | undefined>(undefined);

/**
 * References the SuperHub root element that should be used as the container
 * for overlays like tooltips and dropdowns (e.g., Tippy appendTo target).
 */
export function useSuperHubAppendToRootContext() {
  const value = useContext(SuperHubAppendToRootContext);
  return value === undefined ? null : value;
}

interface AppendToRootProviderProps extends Omit<ComponentPropsWithoutRef<'div'>, 'ref'> {
  children: ReactNode;
}

/**
 * Provides a scoped appendTo context for a panel or section.
 * Child components using useSuperHubAppendToRootContext() will receive
 * this provider's element instead of any parent provider's element.
 *
 * This allows multiple panels (AIPanel, Settings) to each have their own
 * appendTo target without conflicting with each other.
 */
export function AppendToRootProvider({ children, ...divProps }: AppendToRootProviderProps) {
  const [appendTo, setAppendTo] = useState<HTMLDivElement | null>(null);
  const captureAppendTo = useCallback((el: HTMLDivElement | null) => setAppendTo(el), []);

  return (
    <SuperHubAppendToRootContext.Provider value={appendTo}>
      <div ref={captureAppendTo} {...divProps}>
        {children}
      </div>
    </SuperHubAppendToRootContext.Provider>
  );
}
